import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Video, Users, PlayCircle, Map, Loader2 } from "lucide-react";
import { CREATOR_HOUSE as T } from "@/constants/testIds";
import { startCreatorHouseCheckout } from "@/lib/checkout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const IMG = {
  hero: "https://customer-assets-lxgj4vgw.emergentagent.net/job_purchase-creator-1/artifacts/idym1zit_35mm%20Film%20Photos%2012.webp",
  filming: "https://customer-assets-lxgj4vgw.emergentagent.net/job_purchase-creator-1/artifacts/c24ijtts_35mm%20Film%20Photos.webp",
  gym: "https://customer-assets-lxgj4vgw.emergentagent.net/job_purchase-creator-1/artifacts/3zav9o9p_FullSizeRender_VSCO.JPG",
  studio: "https://sawyerfast.ca/images/sawyer-5.webp",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const viewport = { once: true, amount: 0.25 };

const Eyebrow = ({ children, className = "" }) => (
  <p className={`font-body text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] ${className}`}>
    {children}
  </p>
);

const weeks = [
  {
    n: "01",
    label: "YOUR CALLING",
    heading: "Step into your calling + build a brand around what actually makes you you.",
    body:
      "We'll get clear on your purpose, passion, skills, character + story and use them to define the identity behind your personal brand — with a strategy built specifically for fitness + wellness, so you can stop trying to apply generic content advice to your brand.",
  },
  {
    n: "02",
    label: "YOUR CONTENT",
    heading: "Create content that actually captures attention.",
    body:
      "You'll learn how to shoot + curate aesthetic fitness content, develop a recognizable content style, and use stronger visuals, hooks + messaging so your content actually captures attention — and gives people a reason to stay.",
  },
  {
    n: "03",
    label: "YOUR SYSTEM",
    heading: "Stop wondering what the heck you're supposed to post.",
    body:
      "I'll teach you a repeatable system for finding, creating + filming strategic content so you're not relying on random inspiration every time you open Instagram.",
  },
  {
    n: "04",
    label: "YOUR 90-DAY ROADMAP",
    heading: "Know exactly what you're doing when these four weeks end.",
    body:
      "We'll turn everything you've built into a clear 90-day content strategy so you can stop overthinking, start executing, study what's working and continue getting better.",
  },
];

const walkAway = [
  "A clear personal brand direction",
  "A recognizable content style",
  "A repeatable system for creating content",
  "A bank of strategic content ideas",
  "A clear understanding of what to focus on when you post",
  "A 90-day strategy to keep building + growing after the cohort ends",
  "The skills to shoot + curate aesthetic fitness content",
];

const included = [
  {
    Icon: Video,
    title: "4 LIVE GROUP COACHING CALLS WITH SAWYER",
    body:
      "One live call each week where I'll personally teach you the week's strategy + help you apply it to your own brand.",
  },
  {
    Icon: Users,
    title: "PRIVATE CREATOR COMMUNITY",
    body:
      "A space for us to stay connected throughout the four weeks, ask questions, share your content + build alongside the other girls inside the Creator House.",
  },
  {
    Icon: PlayCircle,
    title: "LIVE CALL REPLAYS",
    body:
      "Every weekly training will be recorded so you can go back through what we covered whenever you need it.",
  },
  {
    Icon: Map,
    title: "YOUR 90-DAY CONTENT ROADMAP",
    body:
      "You'll finish our four weeks knowing exactly what to focus on next, so you don't leave inspired just to go straight back to wondering what to post.",
  },
];

export default function CreatorHouse() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState("");

  const openJoin = useCallback(() => {
    setError("");
    setFieldError("");
    setOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (submitting) return;
      const name = firstName.trim();
      const mail = email.trim();
      if (!name) {
        setFieldError("Please enter your first name.");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
        setFieldError("Please enter a valid email address.");
        return;
      }
      setFieldError("");
      setSubmitting(true);
      try {
        await startCreatorHouseCheckout({ firstName: name, email: mail });
      } catch (err) {
        setError("Something went wrong starting checkout. Please try again.");
        setSubmitting(false);
      }
    },
    [firstName, email, submitting]
  );

  const CTAButton = ({ children, testId, variant = "dark", className = "" }) => {
    const base =
      "group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-semibold font-body transition-[transform,background-color] duration-300 hover:scale-[1.03]";
    const styles =
      variant === "cream"
        ? "bg-cream text-ink hover:bg-white"
        : "bg-sage text-white hover:bg-sage-dark";
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={openJoin}
        className={`${base} ${styles} ${className}`}
      >
        {children}
        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
      </button>
    );
  };

  return (
    <div data-testid={T.page} className="font-body bg-cream min-h-screen text-ink antialiased">
      {/* SECTION 1 — HERO */}
      <section className="bg-cream overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="lg:col-span-7"
          >
            <Eyebrow className="text-clay mb-5">THE CALLED CREATOR HOUSE</Eyebrow>
            <h1
              data-testid={T.heroTitle}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-ink"
            >
              4 weeks to build your fitness personal brand + a content system that actually grows it.
            </h1>
            <p className="mt-7 text-lg text-stone-text max-w-xl leading-relaxed">
              You know God is calling you to more.
            </p>
            <p className="mt-4 text-lg text-stone-text max-w-xl leading-relaxed">
              The Called Creator House is a 4-week live creator cohort where I'll personally help you turn your passion for fitness into a clear personal brand, create content that captures attention, and build a strategy you can actually execute.
            </p>
            <div className="mt-9">
              <CTAButton testId={T.heroCta}>JOIN THE CREATOR HOUSE — $999 USD</CTAButton>
              <p className="mt-4 text-sm text-stone-muted">One-time payment • 4-week live cohort</p>
            </div>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15 }}
            className="lg:col-span-5"
          >
            <img
              alt="Sawyer Fast"
              className="rounded-2xl object-cover aspect-[4/5] w-full shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              src={IMG.hero}
            />
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — THE TRANSFORMATION */}
      <section className="bg-white border-y border-line">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            <h2
              data-testid={T.transformationTitle}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight"
            >
              stop guessing. start building.
            </h2>
            <div className="mt-7 space-y-5 text-base sm:text-lg text-stone-text leading-relaxed max-w-xl">
              <p>You don't need another folder full of saved content tips.</p>
              <p>
                You need to know what makes you worth following, what your content should actually look like, how to consistently come up with strategic ideas, and what to do long enough to actually get good at it.
              </p>
              <p className="font-semibold text-ink">
                That's what we're building together over the next four weeks.
              </p>
            </div>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            transition={{ delay: 0.12 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <img
              alt="Creating content in the studio"
              className="rounded-2xl object-cover aspect-[4/5] w-full shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              src={IMG.filming}
            />
          </motion.div>
        </div>
      </section>

      {/* SECTION 3 — THE FOUR WEEKS */}
      <section className="bg-cream">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="max-w-2xl">
            <Eyebrow className="text-clay mb-4">HERE'S WHAT WE'RE BUILDING</Eyebrow>
            <h2 data-testid={T.weeksTitle} className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">
              The four weeks
            </h2>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
            {weeks.map((w, i) => (
              <motion.div
                key={w.n}
                data-testid={T.weekCard(w.n)}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                transition={{ delay: (i % 2) * 0.1 }}
                className="rounded-2xl border border-line bg-white p-8 sm:p-10 transition-shadow duration-300 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-heading text-5xl sm:text-6xl leading-none text-clay">{w.n}</span>
                  <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-stone-muted">
                    WEEK {w.n} — {w.label}
                  </span>
                </div>
                <h3 className="mt-6 font-heading text-2xl sm:text-3xl leading-snug text-ink">{w.heading}</h3>
                <p className="mt-4 text-base text-stone-text leading-relaxed">{w.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — THE BIGGER PICTURE */}
      <section className="bg-sage">
        <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
            <h2
              data-testid={T.biggerPictureTitle}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight"
            >
              THIS IS JUST THE BEGINNING.
            </h2>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-cream/85 leading-relaxed">
              <p>A personal brand can become so much bigger than followers.</p>
              <p>
                It can open the door to brand deals, digital products, your own business, opportunities, income + the freedom to build a life that actually feels like yours.
              </p>
              <p>But first, you need the brand.</p>
              <p>
                You need people paying attention. You need content that reaches them. And you need a reason for them to stick around.
              </p>
              <p>That's what these four weeks are about.</p>
              <p className="font-semibold text-white">
                We're building the foundation first — so you actually have something to monetize later.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 — WHAT YOU'LL WALK AWAY WITH */}
      <section className="bg-cream">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
              <h2
                data-testid={T.walkAwayTitle}
                className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight"
              >
                by the end of the Creator House, you'll have:
              </h2>
            </motion.div>
            <div className="mt-10 space-y-4">
              {walkAway.map((item, i) => (
                <motion.div
                  key={item}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={viewport}
                  transition={{ delay: i * 0.06 }}
                  className="flex items-start gap-4 rounded-2xl border border-line bg-white p-5"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage">
                    <Check className="h-4 w-4 text-white" aria-hidden="true" />
                  </span>
                  <p className="text-base sm:text-lg text-ink leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </div>
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="mt-6 rounded-2xl border border-clay/40 bg-sand p-7 sm:p-8"
            >
              <p className="font-heading text-xl sm:text-2xl leading-snug text-ink">
                And most importantly, the confidence + direction to start making an impact and step into what you feel God calling you to do.
              </p>
            </motion.div>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={viewport}
              className="mt-10 font-accent text-3xl sm:text-4xl text-clay leading-snug"
            >
              not 400 random content tips. a simple system you can actually follow.
            </motion.p>
          </div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            transition={{ delay: 0.12 }}
            className="lg:col-span-5 lg:sticky lg:top-16"
          >
            <img
              alt="Sawyer Fast"
              className="rounded-2xl object-cover aspect-[4/5] w-full shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              src={IMG.studio}
            />
          </motion.div>
        </div>
      </section>

      {/* SECTION 6 — WHAT'S INCLUDED */}
      <section className="bg-sand border-y border-line">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="max-w-2xl">
            <h2
              data-testid={T.includedTitle}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink"
            >
              everything inside the Creator House
            </h2>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
            {included.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                transition={{ delay: (i % 2) * 0.1 }}
                className="rounded-2xl border border-line bg-white p-8 sm:p-10"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-cream border border-line">
                  <Icon className="h-5 w-5 text-clay" aria-hidden="true" />
                </span>
                <h3 className="mt-6 font-body text-sm font-semibold uppercase tracking-[0.14em] text-ink leading-relaxed">
                  {title}
                </h3>
                <p className="mt-3 text-base text-stone-text leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHO THIS IS FOR */}
      <section className="bg-cream">
        <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            transition={{ delay: 0.12 }}
            className="lg:col-span-5"
          >
            <img
              alt="Training in the studio"
              className="rounded-2xl object-cover aspect-[4/5] w-full shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              src={IMG.gym}
            />
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="lg:col-span-7"
          >
            <h2
              data-testid={T.whoTitle}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink leading-tight"
            >
              this is for the girl who knows she's ready to take content seriously.
            </h2>
            <div className="mt-7 space-y-5 text-base sm:text-lg text-stone-text leading-relaxed max-w-xl">
              <p>You love fitness, health + wellness.</p>
              <p>You know you're called to something more.</p>
              <p>
                Maybe you've already started posting. Maybe you've been thinking about starting for way too long.
              </p>
              <p>
                Either way, you're done waiting until you're more confident, your life looks more aesthetic, more fit, less busy, or somehow magically "ready."
              </p>
              <p>You don't need to have your entire brand figured out before you start.</p>
              <p className="font-semibold text-ink">That's literally what we're going to figure out together.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 8 — FINAL OFFER / CHECKOUT */}
      <section className="bg-sage">
        <div className="max-w-3xl mx-auto px-6 py-28 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport}>
            <Eyebrow className="text-cream/70 mb-5">THE CALLED CREATOR HOUSE</Eyebrow>
            <h2
              data-testid={T.finalTitle}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-tight"
            >
              let's build the brand you've been thinking about building.
            </h2>
            <p className="mt-7 text-base sm:text-lg text-cream/85 leading-relaxed max-w-xl mx-auto">
              4 weeks of live coaching, strategy + implementation designed to give you the foundation and content system to finally take your personal brand seriously.
            </p>

            <div className="mt-10 inline-flex flex-col items-center">
              <span className="font-heading text-5xl sm:text-6xl text-white leading-none">$999 USD</span>
              <span className="mt-3 font-body text-sm uppercase tracking-[0.2em] text-cream/70">
                One-time payment
              </span>
            </div>

            <div className="mt-10 flex flex-col items-center">
              <CTAButton testId={T.finalCta} variant="cream">JOIN THE CREATOR HOUSE</CTAButton>
              <p className="mt-4 text-sm text-cream/70">4-week live cohort</p>
              <p className="mt-1 text-sm text-cream/60">Live cohort begins October 1st</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* JOIN — name + email step before checkout */}
      <Dialog open={open} onOpenChange={(v) => !submitting && setOpen(v)}>
        <DialogContent
          data-testid={T.joinDialog}
          className="bg-cream border-line rounded-2xl sm:max-w-md p-8"
        >
          <DialogHeader>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-clay">
              THE CALLED CREATOR HOUSE
            </p>
            <DialogTitle className="font-heading text-3xl font-normal tracking-tight text-ink leading-tight mt-2">
              a couple quick details
            </DialogTitle>
            <DialogDescription className="text-stone-text text-base leading-relaxed">
              This is where your welcome + all your Creator House details will go.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-2 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="ch-first-name" className="text-sm font-semibold text-ink">
                First name
              </Label>
              <Input
                id="ch-first-name"
                data-testid={T.joinFirstName}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                autoComplete="given-name"
                className="bg-white border-line rounded-xl h-12 text-ink placeholder:text-stone-muted focus-visible:ring-clay"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ch-email" className="text-sm font-semibold text-ink">
                Email
              </Label>
              <Input
                id="ch-email"
                data-testid={T.joinEmail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                autoComplete="email"
                className="bg-white border-line rounded-xl h-12 text-ink placeholder:text-stone-muted focus-visible:ring-clay"
              />
            </div>

            {fieldError && (
              <p data-testid={T.joinError} className="text-sm text-red-700">
                {fieldError}
              </p>
            )}
            {error && (
              <p data-testid={T.checkoutError} className="text-sm text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid={T.joinSubmit}
              disabled={submitting}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage px-8 py-4 text-base font-semibold font-body text-white transition-[transform,background-color] duration-300 hover:bg-sage-dark hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  Redirecting to secure checkout…
                </>
              ) : (
                <>
                  Continue to secure checkout — $999 USD
                  <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </>
              )}
            </button>
            <p className="text-center text-xs text-stone-muted">
              One-time payment • Secure checkout powered by Stripe
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
